// ADR-0007 U7/U10: wires the SSE client into the app. Owns the connection-state store (KTD12) and routes
// events. Per KTD7, SSE is a HINT: rather than merge differently-shaped push payloads into the stores, an
// order/position/autotrader event triggers a debounced refetch of authoritative state via the existing REST
// path. This keeps the store the single source of truth (no drift) and makes the push purely a low-latency
// "something changed for you" signal. The connection-state store drives the shell indicator.

import { writable, get } from 'svelte/store';
import { SseClient, type SseConnectionState, type SseFrame } from './sseClient.js';
import { selectedOrgId } from './orgContext.js';
import { tradingContext, venueScopeFor } from './venueContext.js';
import { streamModeFilter } from './runContext.js';

/** The shell connection indicator state (KTD12): connecting | connected | reconnecting | stale. */
export const sseConnectionState = writable<SseConnectionState>('connecting');

/** The latest backtest progress per channel id (KTD12 demux map), for the progress UI. */
export interface BacktestProgressState {
  channelId: string;
  runId: string;
  percent: number;
  phase: string;
  /** Per-candle position from the server payload — drives the "candle X of Y" count (R5). */
  candleIndex: number;
  totalCandles: number;
  /** Phase-specific caption detail (the symbol being collected during 'collecting', TASK-20260728). */
  message: string | null;
}
export const backtestProgress = writable<Map<string, BacktestProgressState>>(new Map());

/** The most recent autotrader NAV mark per request id (U10). */
export const autotradeNav = writable<Map<string, unknown>>(new Map());

/** A capped list of recent, persistent autotrader alerts (U10/KTD12 — not auto-dismissed). */
export interface AutotradeAlert {
  seq: number;
  code: string;
  message: string;
  strategyId?: string;
  /** Severity name (`Info`/`Warning`/`Error`/`Critical`) — the loud banner + Errors view gate on `>= Error` (plan U8). */
  severity: string;
  /** The run mode the alert belongs to ('Paper' | 'Live', ADR-0040 M12), or null for a mode-agnostic alert. */
  mode: string | null;
  at: number;
}
export const autotradeAlerts = writable<AutotradeAlert[]>([]);

/**
 * A bare counter bumped on every `twap-progress` frame (TASK-20260823-twap-strategies-tca AC 24). The TWAP
 * tab and the per-parent report refetch their AUTHORITATIVE reads (`GET /api/twap/parents…`) on this hint;
 * the event's own numbers are never stored here (KTD7 — SSE is a hint, not a source of financial state).
 */
export const twapTickSeq = writable<number>(0);

/**
 * Drop every persisted alert belonging to a strategy that is no longer present (removed / undeployed). Called
 * after the strategy list reloads so a removed strategy's faults don't linger in the feed — and can never
 * resurface if its id were ever reselected. `keepIds` is the set of strategy ids still in the list; an alert
 * with no strategyId (ownerless) is kept (it is never rendered against a specific strategy anyway).
 */
export function pruneAlertsToStrategies(keepIds: Iterable<string>): void {
  const keep = new Set(keepIds);
  autotradeAlerts.update((list) => list.filter((a) => !a.strategyId || keep.has(a.strategyId)));
}

let client: SseClient | null = null;
let connectedOrgId: string | null = null;
let connectedScope: string | null = null; // the (venue set, mode filter) key, e.g. '2|Paper' or '6|*' (ADR-0040 M13)
let refetchTimer: ReturnType<typeof setTimeout> | null = null;

/** The connection's scope key: the comma-joined venue set and the mode filter ('*' = unscoped). */
function scopeKey(venueScope: string, mode: string | null): string {
  return `${venueScope}|${mode ?? '*'}`;
}

export interface SseHostCallbacks {
  /** Refetch authoritative order + position state (called on connect and debounced on order/nav events). */
  refetchAuthoritative: () => void | Promise<void>;

  /**
   * An adjustment-status push arrived (U10/U14): the Pending→Applied/Failed lifecycle for a runtime capital or
   * params adjustment. The host reconciles its pending-adjustment map from the (log-safe) payload. Optional —
   * dashboards that don't render the allocation strip omit it.
   */
  onAdjustmentStatus?: (payload: unknown) => void;

  /**
   * A NAV tick arrived (U12b live half): the transparency tab refetches its signal series on this hint (the
   * same KTD7 hint-then-refetch pattern the dashboard uses for NAV/orders). Optional — only the Candles &
   * Signals tab wires it; debounced together with the authoritative refetch.
   */
  onNavTick?: () => void;

  /**
   * A decision-lineage row landed (TASK-20260728-monthly-export-decision-sse): the log-safe autotrade-decision
   * WATERMARK. The Decisions/Execution/Book tabs refetch their membership-gated reads on this hint (a real
   * decision, not the coarser NAV cadence). Optional — only the observability tabs wire it; each tab debounces
   * its own refetch, and the NAV-tick fallback stays as belt-and-suspenders. Payload is log-safe (keys only).
   */
  onDecision?: (payload: unknown) => void;

  /**
   * A TWAP parent or child row changed (TASK-20260823-twap-strategies-tca AC 24): the log-safe
   * `twap-progress` payload (parentOrderId, requestId, strategyId, source, symbol, side, status, cycle
   * counts, filled/target qty + amount, last child id/status, changeSeq, updatedAt). Routed per org by the
   * producer. Consumers use it as a REFETCH hint keyed on parentOrderId/requestId — financial figures on
   * screen always come from the authoritative read, never from this payload. Optional.
   */
  onTwapProgress?: (payload: unknown) => void;
}

// ---------------------------------------------------------------------------------------------------------
// Subscriber registry. The app holds ONE SSE connection (per org); views REGISTER their callbacks instead of
// replacing the connection. The previous design was a last-caller-wins singleton — App.svelte, PaperTrading,
// and LiveTrading each called startSse(theirCallbacks), so (a) every dashboard switch tore down + re-opened
// the connection (token re-acquire + reconnect + full refetch churn — the trigger correlated with the UI
// freeze repro), and (b) whichever view mounted LAST silently stole event routing from the others (orders/
// positions stopped SSE-refreshing the moment a trading dashboard mounted). With the registry, switching
// dashboards swaps subscribers in O(1) with zero connection churn, and every registered view keeps receiving
// its events.
// ---------------------------------------------------------------------------------------------------------
const subscribers = new Set<SseHostCallbacks>();

/**
 * Register a view's SSE callbacks. Returns an unsubscribe function — call it in onDestroy. Registration does
 * not open the connection; pair with `ensureSse()` (idempotent) on mount.
 */
export function addSseSubscriber(callbacks: SseHostCallbacks): () => void {
  subscribers.add(callbacks);
  return () => subscribers.delete(callbacks);
}

/**
 * Ensure the single app-level SSE connection is open and scoped to the CURRENT `selectedOrgId` and run
 * scope. Idempotent: already connected for this org + scope → no-op (this is what makes dashboard switches
 * churn-free). A different org or scope (or no connection) → tear down and reconnect. No org yet → no-op
 * (call again when the org context resolves).
 */
export function ensureSse(): void {
  const orgId = get(selectedOrgId);
  if (!orgId) {
    return; // org context not ready yet
  }
  // The venue SCOPE is a SET keyed by the header context (ADR-0012 D9): Live subscribes {2}, Sandbox {6},
  // sent as the comma form the server parses. Since ADR-0040 Paper and Live runs on Coinbase share venue 2
  // and differ by the event's `mode`; a mode-specific view (the Paper Trading dashboard) narrows the stream
  // with the optional `?mode=` filter (M12) — mode-agnostic events still flow. The reconnect keys on BOTH.
  const venueScope = venueScopeFor(get(tradingContext)).join(',');
  const mode = get(streamModeFilter);
  const scope = scopeKey(venueScope, mode);
  if (client && connectedOrgId === orgId && connectedScope === scope) {
    return; // already connected for this org + (venue, mode) scope — no churn.
  }

  // Switching org OR scope (or first connect after a prior one): drop the previous scope's persisted alerts.
  // They were fanned out to the PRIOR org/scope session and must never bleed into the new context's
  // banner/Errors views — the server scopes the feed (org + venue set + mode), but the client store is shared,
  // so a stale org-A (or other-context) order_rejected would otherwise linger until the 50-cap evicted it. The
  // new connection refetches its own authoritative state via onConnected.
  if (connectedOrgId !== null && (connectedOrgId !== orgId || connectedScope !== scope)) {
    autotradeAlerts.set([]);
  }

  stopSse();
  connectedOrgId = orgId;
  connectedScope = scope;
  client = new SseClient({
    orgId,
    exchange: venueScope,
    mode,
    onStateChange: (state) => sseConnectionState.set(state),
    onConnected: () => {
      // KTD7: on every (re)connect, every registered view refetches authoritative state to cover the gap.
      for (const s of [...subscribers]) void s.refetchAuthoritative();
    },
    onEvent: (frame) => routeEvent(frame),
  });
  client.start();
}

/** Tear down the SSE connection (logout / unmount). Subscribers stay registered; a later ensureSse() resumes. */
export function stopSse(): void {
  client?.close();
  client = null;
  connectedOrgId = null;
  connectedScope = null;
  if (refetchTimer) {
    clearTimeout(refetchTimer);
    refetchTimer = null;
  }
}

// A mode-specific view setting or clearing the filter re-scopes an OPEN connection (ADR-0040 M13: every
// SSE reconnect keys on (venue, mode)). Nothing to do while disconnected — the next ensureSse() reads the
// filter itself. The store owns this reaction so no view has to remember to reconnect after it unmounts.
streamModeFilter.subscribe(() => {
  if (client) ensureSse();
});

function routeEvent(frame: SseFrame): void {
  let payload: any;
  try {
    payload = JSON.parse(frame.data);
  } catch {
    return; // ignore malformed frames
  }

  switch (frame.event) {
    case 'order':
      // Order status/fill changed for this tenant — refetch authoritative orders/positions (debounced).
      scheduleRefetch();
      break;

    case 'backtest-progress':
      applyBacktestProgress(payload);
      break;

    case 'autotrade-order':
      // A fill/terminal autotrader order — refetch authoritative state (KTD7) for financial accuracy.
      scheduleRefetch();
      break;

    case 'autotrade-nav':
      if (payload?.RequestId) {
        autotradeNav.update((m) => {
          const next = new Map(m);
          next.set(String(payload.RequestId), payload);
          return next;
        });
      }
      // SSE is a HINT (KTD7): the NAV event's own numbers are low-latency, but the dashboard's metrics/charts
      // must refresh from the AUTHORITATIVE refetch (getStrategy + getStrategyPerformance), not the event
      // payload. Without this, live metrics only refreshed on reconnect/backstop. Debounced so a burst of NAV
      // bars coalesces into one refetch; the host's refetchAuthoritative captures $selectedOrgId at trigger
      // time and drops a stale-org response (org-switch race guard, U3/U6).
      scheduleRefetch();
      // U12b live half: nudge the transparency tab to refresh its signal series on the same NAV hint.
      for (const s of [...subscribers]) s.onNavTick?.();
      break;

    case 'autotrade-alert':
      applyAutotradeAlert(payload);
      break;

    case 'autotrade-adjustment':
      // The runtime-adjustment lifecycle (U10/U14): a Pending→Applied/Failed transition for a capital or params
      // adjustment, routed to the actor's org key. The host flips its pending-strip row and refetches the
      // authoritative per-org slice (an applied deposit/withdraw changes NAV). Log-safe payload (no balances).
      for (const s of [...subscribers]) s.onAdjustmentStatus?.(payload);
      if (payload?.status === 'Applied' || payload?.Status === 'Applied') {
        scheduleRefetch();
      }
      break;

    case 'autotrade-decision':
      // A new decision-lineage row landed (this task): fan the log-safe watermark to the observability tabs so
      // they refetch their authoritative reads on a REAL decision instead of only the NAV-tick hint. No
      // financial refetch here — decisions don't move NAV; the tabs pull their own membership-gated data.
      for (const s of [...subscribers]) s.onDecision?.(payload);
      break;

    case 'twap-progress':
      // A TWAP parent/child changed (AC 24). Fan the log-safe watermark to the TWAP tab / report subscribers and
      // bump the tick store; they debounce their own scoped authoritative refetch (org + strategy captured at
      // send, compared at land — lesson 2026-08-18). Nothing from the payload is stored: the progress numbers
      // are a hint, the list/detail reads are the truth.
      for (const s of [...subscribers]) s.onTwapProgress?.(payload);
      twapTickSeq.update((n) => n + 1);
      break;

    default:
      break;
  }
}

/** Test-only: drive {@link routeEvent} with a synthetic frame to assert the event dispatch (no live connection). */
export function __routeEventForTest(frame: SseFrame): void {
  routeEvent(frame);
}

// Coalesce bursts of order events into a single refetch (~400ms) so a fast scanner cadence doesn't
// hammer the REST API. Fans out to every registered subscriber (each one's refetch is cheap-no-op when it
// has nothing selected).
function scheduleRefetch(): void {
  if (refetchTimer) {
    return;
  }
  refetchTimer = setTimeout(() => {
    refetchTimer = null;
    for (const s of [...subscribers]) void s.refetchAuthoritative();
  }, 400);
}

function applyBacktestProgress(payload: any): void {
  const channelId = payload?.ChannelId;
  if (!channelId) {
    return;
  }
  backtestProgress.update((m) => {
    const next = new Map(m);
    // Bounded (review W8): the view evicts its own channel on terminal, but a terminal consumed while
    // the view is unmounted would otherwise linger for the whole session. Insertion order = age.
    if (next.size > 20) {
      const oldest = next.keys().next().value;
      if (oldest !== undefined && oldest !== channelId) next.delete(oldest);
    }
    next.set(channelId, {
      channelId,
      runId: payload.RunId,
      percent: payload.Percent ?? 0,
      phase: payload.Phase ?? 'progress',
      candleIndex: payload.CandleIndex ?? 0,
      totalCandles: payload.TotalCandles ?? 0,
      // Phase-specific caption detail (the symbol being collected during the 'collecting' phase).
      message: payload.Message ?? null,
    });
    return next;
  });
}

function applyAutotradeAlert(payload: any): void {
  if (!payload?.Code) {
    return;
  }
  autotradeAlerts.update((list) => {
    const next: AutotradeAlert = {
      seq: payload.Seq ?? 0,
      code: payload.Code,
      message: payload.Message ?? '',
      strategyId: payload.StrategyId,
      // Default a missing severity to 'Error' (fail loud, KTD8) so a pre-migration row still surfaces.
      severity: payload.Severity ?? 'Error',
      // ADR-0040 M12: the producer stamps the strategy's run mode (null for an account-level alert).
      mode: payload.Mode ?? payload.mode ?? null,
      at: Date.now(),
    };
    // Persistent (not auto-dismissed); cap the list so it doesn't grow unbounded.
    return [next, ...list].slice(0, 50);
  });
}
